/**
 * Analytics Service
 *
 * Centralized analytics tracking for user actions.
 * Fires to Firebase Analytics (GA4) + PostHog simultaneously.
 *
 * Usage:
 * import { analytics } from '@/services/analytics';
 * analytics.morningCheckIn('grateful');
 */

import posthog from 'posthog-js';
import { logEvent } from 'firebase/analytics';
import { analytics as firebaseAnalytics } from './firebase/config';

// ── PostHog init ────────────────────────────────────────────────────────────

const posthogKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const posthogHost = import.meta.env.VITE_POSTHOG_HOST as string | undefined;

if (posthogKey) {
  posthog.init(posthogKey, {
    api_host: posthogHost ?? 'https://us.i.posthog.com',
    session_recording: {
      recordCrossOriginIframes: false,
    },
    disable_session_recording: import.meta.env.DEV,
    // Strip auth tokens from captured URLs
    sanitize_properties: (properties) => {
      if (typeof properties['$current_url'] === 'string') {
        try {
          const url = new URL(properties['$current_url'] as string);
          url.searchParams.delete('token');
          url.searchParams.delete('oobCode');
          url.searchParams.delete('apiKey');
          properties['$current_url'] = url.toString();
        } catch {
          // Invalid URL — leave as-is
        }
      }
      return properties;
    },
    autocapture: false,
    capture_pageview: true,
    loaded: () => {
      if (import.meta.env.DEV) {
        console.log('📊 PostHog initialized');
      }
    },
  });
}

// ── Analytics event types ───────────────────────────────────────────────────

export type AnalyticsEvent =
  // Authentication
  | 'sign_up'
  | 'login'
  | 'logout'
  // Onboarding
  | 'onboarding_started'
  | 'onboarding_completed'
  | 'onboarding_step_completed'
  // Check-ins
  | 'morning_checkin_completed'
  | 'nightly_review_completed'
  // Step Work
  | 'step_started'
  | 'step_completed'
  | 'step_submitted_for_review'
  | 'reflection_saved'
  // Homework
  | 'homework_assigned'
  | 'homework_completed'
  | 'template_created'
  | 'template_shared'
  // Journal
  | 'journal_entry_created'
  | 'journal_entry_viewed'
  // Meetings
  | 'meeting_saved'
  | 'meeting_deleted'
  | 'attendance_logged'
  | 'service_position_added'
  | 'meeting_searched'
  | 'meeting_details_viewed'
  // Sponsor
  | 'sponsor_connected'
  | 'sponsor_message_sent'
  // Settings
  | 'settings_updated'
  | 'sobriety_date_added'
  | 'program_added'
  // Revenue / subscription
  | 'trial_started'
  | 'premium_upgraded'
  | 'billing_page_viewed'
  | 'checkout_started'
  // Landing page / acquisition
  | 'landing_page_viewed'
  | 'landing_cta_clicked'
  | 'pricing_section_viewed'
  // Engagement
  | 'check_in_streak_broken'
  | 'feature_first_used'
  // Errors
  | 'error_occurred'
  // Page views
  | 'page_view';

interface AnalyticsEventParams {
  [key: string]: string | number | boolean | undefined;
}

// ── Core tracking function ──────────────────────────────────────────────────

/**
 * Track an analytics event — fires to both Firebase Analytics and PostHog.
 */
export function trackEvent(
  eventName: AnalyticsEvent,
  params?: AnalyticsEventParams
): void {
  try {
    if (import.meta.env.DEV) {
      console.log(`📊 Analytics: ${eventName}`, params);
    }

    // Firebase Analytics (GA4)
    // Cast to string: logEvent has strict overloads for standard GA4 events,
    // but accepts any string for custom events at runtime.
    if (firebaseAnalytics) {
      logEvent(firebaseAnalytics, eventName as string, params);
    }

    // PostHog
    if (posthogKey) {
      posthog.capture(eventName, params);
    }
  } catch (error) {
    console.error('Analytics tracking error:', error);
  }
}

// ── Page view tracking ──────────────────────────────────────────────────────

export function trackPageView(pageName: string, pageParams?: AnalyticsEventParams): void {
  trackEvent('page_view', { page_name: pageName, ...pageParams });
}

// ── User identity ───────────────────────────────────────────────────────────

/**
 * Identify user in PostHog for cohort analysis.
 * Call after login/registration with the Firebase UID.
 */
export function identifyUser(
  uid: string,
  properties?: { isPremium?: boolean; isInTrial?: boolean; fellowship?: string }
): void {
  try {
    if (posthogKey) {
      posthog.identify(uid, properties);
    }
  } catch (error) {
    console.error('Analytics identify error:', error);
  }
}

/**
 * Reset user identity on logout (important for shared devices).
 */
export function resetUser(): void {
  try {
    if (posthogKey) {
      posthog.reset();
    }
  } catch (error) {
    console.error('Analytics reset error:', error);
  }
}

/**
 * Update user properties for segmentation in PostHog.
 */
export function setUserProperty(propertyName: string, value: string | number): void {
  try {
    if (import.meta.env.DEV) {
      console.log(`👤 User Property: ${propertyName} = ${value}`);
    }
    if (posthogKey) {
      posthog.setPersonProperties({ [propertyName]: value });
    }
  } catch (error) {
    console.error('User property tracking error:', error);
  }
}

// ── Convenience functions ───────────────────────────────────────────────────

export const analytics = {
  // Authentication
  signUp: (method: 'email' | 'google' | 'apple') =>
    trackEvent('sign_up', { method }),

  login: (method: 'email' | 'google' | 'apple') =>
    trackEvent('login', { method }),

  logout: () => {
    trackEvent('logout');
    resetUser();
  },

  // Onboarding
  onboardingStarted: () =>
    trackEvent('onboarding_started'),

  onboardingCompleted: (programs: string[]) =>
    trackEvent('onboarding_completed', {
      programs_count: programs.length,
      programs: programs.join(','),
    }),

  onboardingStepCompleted: (step: string) =>
    trackEvent('onboarding_step_completed', { step }),

  // Check-ins
  morningCheckIn: (mood: string) =>
    trackEvent('morning_checkin_completed', { mood }),

  nightlyReview: (gratitude_count: number) =>
    trackEvent('nightly_review_completed', { gratitude_count }),

  // Step Work
  stepStarted: (step_number: number, fellowship: string) =>
    trackEvent('step_started', { step_number, fellowship }),

  stepCompleted: (step_number: number, fellowship: string) =>
    trackEvent('step_completed', { step_number, fellowship }),

  reflectionSaved: (step_number: number, prompt_id: string) =>
    trackEvent('reflection_saved', { step_number, prompt_id }),

  // Homework
  homeworkAssigned: (step_number: number, type: string) =>
    trackEvent('homework_assigned', { step_number, type }),

  homeworkCompleted: (assignment_id: string) =>
    trackEvent('homework_completed', { assignment_id }),

  templateCreated: (template_name: string) =>
    trackEvent('template_created', { template_name }),

  // Journal
  journalEntryCreated: (has_image: boolean) =>
    trackEvent('journal_entry_created', { has_image }),

  // Meetings
  meetingSaved: (format: string, fellowship: string) =>
    trackEvent('meeting_saved', { format, fellowship }),

  meetingDeleted: () =>
    trackEvent('meeting_deleted'),

  attendanceLogged: (has_role: boolean) =>
    trackEvent('attendance_logged', { has_role }),

  servicePositionAdded: (position_name: string) =>
    trackEvent('service_position_added', { position_name }),

  meetingSearched: (fellowship: string, location?: string) =>
    trackEvent('meeting_searched', { fellowship, location }),

  // Settings
  settingsUpdated: (section: string) =>
    trackEvent('settings_updated', { section }),

  sobrietyDateAdded: (fellowship: string) =>
    trackEvent('sobriety_date_added', { fellowship }),

  // Revenue / subscription
  trialStarted: () =>
    trackEvent('trial_started'),

  premiumUpgraded: (plan: 'monthly' | 'annual_founding' | 'annual_standard') =>
    trackEvent('premium_upgraded', { plan }),

  billingPageViewed: (is_trial: boolean, is_premium: boolean) =>
    trackEvent('billing_page_viewed', { is_trial, is_premium }),

  checkoutStarted: (plan: string) =>
    trackEvent('checkout_started', { plan }),

  // Landing page / acquisition
  landingPageViewed: () =>
    trackEvent('landing_page_viewed'),

  landingCtaClicked: (cta_label: string, section: string) =>
    trackEvent('landing_cta_clicked', { cta_label, section }),

  pricingSectionViewed: () =>
    trackEvent('pricing_section_viewed'),

  // Engagement
  featureFirstUsed: (feature: string) =>
    trackEvent('feature_first_used', { feature }),

  // Errors
  errorOccurred: (error_message: string, component?: string) =>
    trackEvent('error_occurred', { error_message, component }),
};

export default analytics;
